import { cva, type VariantProps } from 'class-variance-authority';
import { Predicate } from 'effect';
import { ChevronRight } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import type { VsLinkProps } from '@vs-shop/headless/vsLink';

import Link from '@vs-shop/image-link/Link';
import Picture, { type PictureProps } from '@vs-shop/image-link/Picture';

import { buttonVariants } from '@vs-shop/ui/buttonVariants';
import { clsxm } from '@vs-shop/utils';

/**
 * The banner section: a promotional surface — image, copy, one click target.
 * Flat named parts a tenant arranges in its own JSX, each painting itself.
 *
 *   <Banner variant={BannerVariant.TINY}>
 *     <BannerImage {...media} alt={title} />
 *     <BannerContent variant={BannerVariant.TINY}>
 *       <BannerBody variant={BannerVariant.TINY} highlightColor={highlight}>
 *         <BannerTitle variant={BannerVariant.TINY}>{title}</BannerTitle>
 *       </BannerBody>
 *       <BannerCta variant={BannerVariant.TINY} href={href} />
 *     </BannerContent>
 *     <BannerLink href={href} aria-label={title} />
 *   </Banner>
 *
 * `variant` is the appearance axis, required on every part that has one. No
 * recipe declares `defaultVariants`, so an appearance is never decided by
 * omission, and the opt-out is a named member — `BannerVariant.UNSTYLED`, whose
 * recipe entry is the empty string — so even the empty appearance reads out of
 * the same list. A part that paints the same in every appearance carries no
 * `variant` at all.
 *
 * New appearances go here, named for the look (`tiny`), never for who asked.
 *
 * Server-first: every part is a plain server component.
 */

/**
 * The section box: positioning context for the media and the overlay link.
 *
 * `hero` is the one appearance that steps on the VIEWPORT rather than on the
 * section's own width, and it is the only one entitled to: the other three are
 * furniture a page places at whatever width it has spare — a grid cell, a
 * panel, a full-bleed band — so they can only step on the box they are given.
 * A hero is by definition the page's leading edge-to-edge band, its scale set
 * by the device rather than by its container, and its height is set here, on
 * the section root, which is the container-query context's PARENT and therefore
 * cannot query itself. The steps are contract breakpoint names, so each tenant
 * theme decides where they land. No arm mixes the two kinds of step.
 */
export const bannerVariants = cva('relative h-full w-full overflow-hidden', {
  variants: {
    variant: {
      base: 'rounded-lg',
      // Edge to edge, so no rounding — and a height per device class.
      hero: 'tab:h-[430px] lap:h-[520px] h-[340px]',
      tiny: 'rounded-lg',
      // Sits flush in the product grid — no rounding.
      product_card: '',
      unstyled: '',
    },
  },
});

type BannerVariantName = NonNullable<VariantProps<typeof bannerVariants>['variant']>;

export type BannerVariant = BannerVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const BannerVariant = {
  BASE: 'base',
  HERO: 'hero',
  TINY: 'tiny',
  PRODUCT_CARD: 'product_card',
  /**
   * The opt-out: a tenant painting the part itself. Every recipe carries it as
   * an empty entry rather than leaving the arm out, so what it paints — nothing
   * — is stated in the list instead of decided by absence from it.
   */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, BannerVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type BannerVariantProps = { readonly variant: BannerVariant };

export type BannerProps = ComponentProps<'div'> & BannerVariantProps;

/**
 * Root of the section, two elements deep: the outer box clips the
 * `object-cover` media, the inner one is the container-query context every
 * `@[...]` step resolves against and the containing block the media and the
 * overlay link position themselves in.
 */
export const Banner = ({ className, variant, children, ...props }: BannerProps) => (
  <div
    data-slot="banner"
    data-variant={variant}
    className={clsxm(bannerVariants({ variant }), className)}
    {...props}
  >
    <div data-slot="banner-viewport" className="@container grid h-full">
      {children}
    </div>
  </div>
);

/**
 * The content column laid over the media. `min-w-0` keeps it from widening the
 * section: it is the grid's only in-flow item, so a long unbreakable child
 * would otherwise raise the track's minimum size and spill out of the clip.
 */
export const bannerContentVariants = cva(
  'text-foreground-inverted relative flex h-full min-w-0 justify-between',
  {
    variants: {
      variant: {
        base: 'min-h-[100px] flex-col gap-6 p-4 @[700px]:min-h-[125px] @[700px]:gap-8',
        // The copy column, inset from the band's left edge and capped so the
        // photograph shows through beside it. `h-auto` and `justify-start`
        // undo the base string: the height belongs to the root here, and the
        // call to action follows the copy instead of being pushed to the foot.
        hero: 'tab:ml-[64px] tab:max-w-2/3 lap:mt-12 lap:ml-[128px] lap:max-w-[calc(66%-128px)] des:ml-[142px] des:max-w-[calc(50%-142px)] mt-6 ml-[32px] h-auto max-w-4/5 flex-col justify-start',
        tiny: 'flex-row p-2 @[500px]:p-3',
        product_card: 'min-h-[537px] flex-col gap-8 px-5 py-6 @[500px]:gap-10 @[500px]:px-8',
        unstyled: '',
      },
    },
  }
);

export type BannerContentProps = ComponentProps<'div'> & BannerVariantProps;

/** The content column: copy on one side, call to action on the other. */
export const BannerContent = ({ className, variant, ...props }: BannerContentProps) => (
  <div
    data-slot="banner-content"
    className={clsxm(bannerContentVariants({ variant }), className)}
    {...props}
  />
);

/** The copy stack, and how wide it runs before the image shows through beside it. */
export const bannerBodyVariants = cva('flex flex-col', {
  variants: {
    variant: {
      base: 'w-4/5 gap-4 @[700px]:w-1/2',
      /**
       * No `gap`, and no width of its own: `hero` insets the whole column at
       * {@link bannerContentVariants}, and its three copy parts sit at three
       * different distances from one another which step independently — an
       * even gap cannot express that, so each part carries its own bottom
       * margin instead.
       */
      hero: '',
      tiny: 'w-full gap-1.5',
      product_card: 'w-full gap-6',
      unstyled: '',
    },
  },
});

/** Soft double glow so copy stays legible over a busy photo. Alpha: `80` ≈ 50%, `40` ≈ 25%. */
export const bannerHighlightShadow = (color: string): string =>
  `0 0 1px ${color}80, 0 0 1px ${color}40`;

export type BannerBodyProps = ComponentProps<'div'> &
  BannerVariantProps & {
    /**
     * Draws {@link bannerHighlightShadow} behind the copy. Set here rather than
     * per text part because `text-shadow` inherits.
     */
    readonly highlightColor?: string;
  };

/** The copy stack: eyebrow, title, description. */
export const BannerBody = ({
  className,
  variant,
  highlightColor,
  style,
  ...props
}: BannerBodyProps) => (
  <div
    data-slot="banner-body"
    className={clsxm(bannerBodyVariants({ variant }), className)}
    style={
      highlightColor === undefined
        ? style
        : { textShadow: bannerHighlightShadow(highlightColor), ...style }
    }
    {...props}
  />
);

export const bannerEyebrowVariants = cva('tracking-extra-wide', {
  variants: {
    variant: {
      base: 'text-base font-medium',
      hero: 'tab:mb-4 tab:text-lg lap:mb-6 lap:text-xl/6 mb-2 text-base font-medium',
      tiny: 'text-xs',
      product_card: 'text-base font-medium',
      unstyled: '',
    },
  },
});

export type BannerEyebrowProps = ComponentProps<'div'> & BannerVariantProps;

/**
 * The short lead-in above the title. Carries its own type scale but no colour:
 * an eyebrow's colour is per-instance CMS data, and CMS styling reaches shared
 * parts only as final CSS values on the native `style` prop.
 */
export const BannerEyebrow = ({ className, variant, ...props }: BannerEyebrowProps) => (
  <div
    data-slot="banner-eyebrow"
    className={clsxm(bannerEyebrowVariants({ variant }), className)}
    {...props}
  />
);

export const bannerTitleVariants = cva('font-heading tracking-tight', {
  variants: {
    variant: {
      base: 'line-clamp-2 text-2xl/7 @[700px]:text-[28px]/8',
      hero: 'tab:mb-3 tab:text-4xl lap:text-5xl mb-2 line-clamp-2 text-[28px]/8',
      tiny: 'line-clamp-2 text-lg/6 font-medium @[500px]:text-xl/6',
      product_card: 'line-clamp-3 text-[28px]/8 @[500px]:text-4xl',
      unstyled: '',
    },
  },
});

export type BannerTitleProps = ComponentProps<'div'> & BannerVariantProps;

/**
 * The headline — a `<div>`, deliberately not a heading element. A banner is
 * furniture a page repeats, nests in a grid and rotates through a carousel; a
 * heading here would enter the document outline and compete with the page's
 * real one. A page that needs this text as its heading composes
 * `@vs-shop/ui/heading` in its own JSX, where the level is a visible decision.
 */
export const BannerTitle = ({ className, variant, ...props }: BannerTitleProps) => (
  <div
    data-slot="banner-title"
    className={clsxm(bannerTitleVariants({ variant }), className)}
    {...props}
  />
);

export const bannerDescriptionVariants = cva('font-heading tracking-tight', {
  variants: {
    variant: {
      base: 'line-clamp-3 text-sm/4 font-medium text-balance @[700px]:text-base/5',
      hero: 'tab:text-lg/6 lap:mb-9 lap:text-xl/7 mb-6 line-clamp-3 text-base/5 font-medium text-balance',
      tiny: 'line-clamp-2 text-sm/4 @[500px]:line-clamp-3 @[500px]:text-base/5',
      product_card: 'line-clamp-5 text-base/5 font-medium @[500px]:text-lg/6',
      unstyled: '',
    },
  },
});

export type BannerDescriptionProps = ComponentProps<'p'> & BannerVariantProps;

/** The supporting copy under the title. */
export const BannerDescription = ({ className, variant, ...props }: BannerDescriptionProps) => (
  <p
    data-slot="banner-description"
    className={clsxm(bannerDescriptionVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The frame the media hangs in, taken out of flow so the section's grid holds
 * exactly one in-flow item. It lands on the `<picture>`, which must never be
 * `display: contents`: that hands the `<source>` children to the grid, which
 * blockifies them into two empty tracks above the copy.
 */
const bannerImageFrameClasses = 'absolute inset-0';

/** The media fills the frame in every appearance. */
const bannerImageClasses = 'h-full w-full object-cover';

/**
 * `Picture`'s props minus the inner-`<img>` attributes: the section owns how
 * its media fills the frame. `className` lands on the `<picture>`.
 */
export type BannerImageProps = Omit<PictureProps, 'imgProps'>;

/** The section's media. No `variant`: it fills the section box in every appearance. */
export const BannerImage = ({ className, sources = [], ...props }: BannerImageProps) => (
  <Picture
    {...props}
    data-slot="banner-image-picture"
    className={clsxm(bannerImageFrameClasses, className)}
    sources={sources.map((source) => ({ ...source, 'data-slot': 'banner-image-source' }))}
    imgProps={{ 'data-slot': 'banner-image', className: bannerImageClasses }}
  />
);

/**
 * The button-shaped call to action. `z-20` puts it above the overlay link so it
 * keeps its own hover and focus; `mt-auto` pins it to the bottom of the stacked
 * appearances whatever precedes it. It must be able to shrink — a CTA that
 * cannot sets the section's minimum width, pushing its own edge past the clip.
 */
export const bannerCtaVariants = cva(
  'z-20 max-w-full text-center break-words uppercase backdrop-blur-lg',
  {
    variants: {
      variant: {
        base: 'mt-auto h-auto min-h-10 w-fit px-4 py-2 text-xs @[700px]:px-10 @[700px]:text-base',
        // No `mt-auto`: in a hero the button reads as the end of the sentence
        // the copy started, so it follows the description rather than being
        // pushed to the foot of the band.
        hero: 'tab:min-h-12 tab:text-base h-auto min-h-10 w-fit py-2 text-xs',
        tiny: '',
        product_card:
          'hover:border-foreground/40 hover:bg-button-secondary-hover hover:text-button-secondary-foreground-hover mt-auto h-auto min-h-12 py-2',
        unstyled: '',
      },
    },
  }
);

/** The chevron `tiny` points with instead of a button. `ml-auto` is `mt-auto`'s row counterpart. */
const bannerCtaIconClasses =
  'text-foreground-inverted ml-auto size-6 shrink-0 self-center stroke-1';

/**
 * `href` is optional: a CMS row can carry button text with nowhere to point,
 * which renders an inert button rather than disappearing.
 */
export type BannerCtaProps = Omit<VsLinkProps, 'href'> &
  BannerVariantProps & { readonly href?: string };

/**
 * Whether a CMS field carries anything the renderer will draw — nullish, a
 * boolean (`{ctaText && ctaText}` hands a part `false`) and blank or
 * whitespace-only text all mean "nothing", and painting a control around any of
 * them leaves an empty, unnamed, focusable anchor on the page.
 *
 * Not `String.isNonEmpty`: the copy arrives as `children`, typed `ReactNode`.
 */
const hasValue = (field: ReactNode): boolean => {
  if (Predicate.isNullish(field) || Predicate.isBoolean(field)) return false;
  return Predicate.isString(field) ? field.trim() !== '' : true;
};

/**
 * Whether a banner paints its own call to action, which decides the overlay
 * link's ARIA treatment: beside a real CTA it is hidden, alone it carries the
 * accessible name. Exported because that answer is needed one level up from
 * {@link BannerCta}, which reads the same {@link hasValue} — two encodings of
 * this rule is how the overlay ends up hidden with nothing else focusable.
 */
export const bannerHasCta = ({
  variant,
  ctaText,
}: BannerVariantProps & { readonly ctaText: string | null }): boolean =>
  variant !== BannerVariant.TINY && hasValue(ctaText);

/**
 * The call to action, in whichever shape the appearance calls for: a
 * button-styled link, or — for `tiny`, where there is no room for one — a
 * chevron. The chevron is decoration (the click target is {@link BannerLink}),
 * so it is hidden from assistive tech and appears only with somewhere to go.
 *
 * A tenant that disagrees passes `variant={BannerVariant.UNSTYLED}` and restyles
 * the link.
 */
export const BannerCta = ({ className, variant, children, href, ...props }: BannerCtaProps) => {
  const hasDestination = hasValue(href);

  if (variant === BannerVariant.TINY) {
    if (!hasDestination) return null;

    return (
      <ChevronRight
        data-slot="banner-cta-icon"
        aria-hidden
        className={clsxm(bannerCtaIconClasses, className)}
      />
    );
  }

  if (!hasValue(children)) return null;

  return (
    <Link
      data-slot="banner-cta"
      href={href ?? ''}
      className={clsxm(
        buttonVariants({ variant: 'ghostInverted' }),
        bannerCtaVariants({ variant }),
        className
      )}
      {...props}
    >
      {children}
    </Link>
  );
};

export type BannerLinkProps = VsLinkProps;

/**
 * The whole-surface click affordance: an overlay anchor laid over the content
 * rather than wrapped around it, because anchors cannot nest — as a sibling it
 * leaves room for a real `<a href>` CTA beside it.
 *
 * ARIA contract, and the reason this is a part rather than a class recipe:
 *
 * - **Only link in the section** — the tenant passes `aria-label`; an overlay
 *   anchor has no text content and would otherwise reach assistive tech unnamed.
 * - **Beside a real CTA** — the tenant passes `aria-hidden`, and this removes
 *   the tab stop to match. A focusable `aria-hidden` element is a keyboard trap
 *   announced as nothing. An explicit `tabIndex` still wins.
 *
 * No `variant`: the overlay is the same box in every appearance.
 */
export const BannerLink = ({ className, tabIndex, ...props }: BannerLinkProps) => {
  const isAriaHidden = props['aria-hidden'] === true || props['aria-hidden'] === 'true';
  const resolvedTabIndex = tabIndex ?? (isAriaHidden ? -1 : undefined);
  // `exactOptionalPropertyTypes` rejects an explicit `undefined` here.
  const tabIndexProp = resolvedTabIndex === undefined ? {} : { tabIndex: resolvedTabIndex };

  return (
    <Link
      data-slot="banner-link"
      className={clsxm('absolute inset-0 z-10', className)}
      {...tabIndexProp}
      {...props}
    />
  );
};
